import React from 'react';
import Image from 'next/image';
import { getAbsoluteMediaUrl, toOptimizableSrc } from '@/core/utils/media';

/** How a product's brand is shown next to its price (product cards and the product page):
 *  - logo: the brand's logo only — brands without one show nothing (the original behaviour)
 *  - name: the brand name as text, the same for every brand
 *  - logo_or_name: the logo, or the name for brands that have no logo */
export type BrandDisplay = 'logo' | 'name' | 'logo_or_name';

interface ProductBrandMarkProps {
    brand?: { name?: string | null; logo_url?: string | null } | null;
    display?: BrandDisplay;
    logoSize?: number;
    color?: string;
    fontSize?: number;
}

export function ProductBrandMark({ brand, display = 'logo', logoSize = 24, color, fontSize = 12 }: ProductBrandMarkProps) {
    if (!brand) return null;
    const useLogo = !!brand.logo_url && display !== 'name';
    if (useLogo) {
        // Fixed-size box (not just a fixed height): brand logos have all kinds of real aspect
        // ratios, and a reserved 3:1 box with object-contain keeps the layout from shifting
        // when each one decodes (a single non-3:1 logo measured 0.735 CLS on PageSpeed).
        // Through the optimizer: merchants upload logos at full size (dlaszwalni's Jack PNG is
        // 1024×579 / 32 KB for a 72×24 box); Next serves SVGs untouched on its own.
        return (
            <span style={{ height: logoSize, width: logoSize * 3 }} className="shrink-0 flex items-center">
                <Image
                    src={toOptimizableSrc(getAbsoluteMediaUrl(brand.logo_url!))}
                    alt={brand.name || ''}
                    width={logoSize * 3}
                    height={logoSize}
                    style={{ width: '100%', height: '100%' }}
                    className="object-contain"
                />
            </span>
        );
    }
    if (display === 'logo' || !brand.name) return null;
    return (
        <span
            className="shrink-0 whitespace-nowrap font-bold uppercase tracking-wider leading-none"
            style={{ color: color || '#64748b', fontSize: `${fontSize}px` }}
        >
            {brand.name}
        </span>
    );
}
